import { Head, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

import ContactForm from "./_form"

interface Customer {
  id: number
  display_name: string
}

interface Option {
  id: number
  name: string
}

interface ContactData {
  id: number
  salutation: string
  first_name: string
  middle_name: string
  last_name: string
  suffix: string
  department: string
  phone: string
  cell_phone: string
  home_phone: string
  fax: string
  email: string
  address: string
  address2: string
  city: string
  state: string
  zip: string
  notes: string
  family_info: string
  birth_month: number | null
  birth_day: number | null
  send_birthday_card: boolean
  sales_rep_id: number | null
  location_id: number | null
  status: number
}

interface LocationOption {
  id: number
  display_name: string
}

interface Props {
  customer: Customer
  contact: ContactData
  users: Option[]
  locations: LocationOption[]
  errors?: Record<string, string[]>
}

export default function Edit({
  customer,
  contact,
  users,
  locations,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: customer.display_name, href: `/customers/${customer.id}` },
    { title: "Contacts", href: `/customers/${customer.id}/contacts` },
    {
      title: "Edit",
      href: `/customers/${customer.id}/contacts/${contact.id}/edit`,
    },
  ]

  const { data, setData, patch, processing, transform } = useForm({
    salutation: contact.salutation || "",
    first_name: contact.first_name || "",
    middle_name: contact.middle_name || "",
    last_name: contact.last_name || "",
    suffix: contact.suffix || "",
    department: contact.department || "",
    phone: contact.phone || "",
    cell_phone: contact.cell_phone || "",
    home_phone: contact.home_phone || "",
    fax: contact.fax || "",
    email: contact.email || "",
    address: contact.address || "",
    address2: contact.address2 || "",
    city: contact.city || "",
    state: contact.state || "",
    zip: contact.zip || "",
    notes: contact.notes || "",
    family_info: contact.family_info || "",
    birth_month: contact.birth_month,
    birth_day: contact.birth_day,
    send_birthday_card: contact.send_birthday_card,
    sales_rep_id: contact.sales_rep_id,
    location_id: contact.location_id,
    status: contact.status || 0,
  })

  transform((data) => ({ contact: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/customers/${customer.id}/contacts/${contact.id}`)
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit Contact - ${customer.display_name}`} />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">Edit Contact</h1>

        <ContactForm
          customerId={customer.id}
          data={data}
          setData={setData}
          errors={errors}
          processing={processing}
          onSubmit={handleSubmit}
          submitLabel="Update Contact"
          users={users}
          locations={locations}
        />
      </div>
    </AppLayout>
  )
}
